/**
 * Lightbox universel pour agrandir une photo (2026-08-31).
 *
 * Usage :
 *   const [lightbox, setLightbox] = useState<string | null>(null);
 *   <img src={photo_url} onClick={() => setLightbox(photo_url)} />
 *   <PhotoLightbox url={lightbox} label="John Doe" onClose={() => setLightbox(null)} />
 *
 * - Fermeture : clic sur overlay, bouton X, ou touche Échap
 * - Photo max 90vw × 90vh, ratio conservé
 * - Optionnel : label sous la photo (nom, date, description)
 * - Zoom natif via pinch-to-zoom sur mobile (touch-none désactivé)
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download } from 'lucide-react';

interface Props {
  url: string | null;
  label?: string;
  hint?: string;
  allowDownload?: boolean;
  onClose: () => void;
}

export const PhotoLightbox: React.FC<Props> = ({
  url, label, hint, allowDownload = false, onClose,
}) => {
  useEffect(() => {
    if (!url) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [url, onClose]);

  if (!url || typeof document === 'undefined') return null;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '2rem 1rem',
        background: 'rgba(0,0,0,0.9)',
        backdropFilter: 'blur(4px)',
      }}
    >
      {/* Header actions */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute top-3 right-3 flex items-center gap-2 z-10"
      >
        {allowDownload && (
          <a href={url} download target="_blank" rel="noreferrer"
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Télécharger">
            <Download size={18} />
          </a>
        )}
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Fermer (Échap)"
        >
          <X size={20} />
        </button>
      </div>

      {/* Photo */}
      <img
        src={url}
        alt={label || 'Photo'}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '90vw', maxHeight: label ? '75vh' : '85vh',
          objectFit: 'contain',
          borderRadius: 8,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.9)',
        }}
      />

      {/* Label sous la photo */}
      {label && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="mt-4 text-center text-white max-w-2xl"
        >
          <div className="font-semibold text-lg">{label}</div>
          {hint && <div className="text-sm text-white/60 mt-1">{hint}</div>}
        </div>
      )}
    </div>,
    document.body
  );
};

export default PhotoLightbox;
